import { alpha, isHexColor, luminance, mix } from "@/common/lib/color";

export const DEFAULT_STACK = {
    primary: "#284D6F",
    emphasis: "#FD951E",
    text: "#000000",
    background: "#FFFFFF"
};

const SOCIAL_LABELS = {
    tiktok: "TikTok",
    youtube: "YouTube",
    twitter: "X",
    x: "X",
    linkedin: "LinkedIn",
    whatsapp: "WhatsApp"
};

// Mismo mecanismo que `useStackColors` en catalog/: los 4 colores del
// tenant se normalizan una sola vez y se exponen como las variables CSS
// `--color-stack-*` que ya mapea tailwind.config.js. Se aplican con un
// unico `:style` en el contenedor raiz del preview; de ahi para abajo todo
// se pinta con clases (`text-stack-ink`, `bg-stack-tile-bg`, etc.), sin
// pasar colores por props. `ink`, `text-sub`, `line`, `tile-bg` y
// `page-bg` no vienen del tenant: se derivan aca de los 4 colores base.
// `ink` es la letra sobre `bg-stack-emphasis` (misma definicion que en
// catalog); sobre el fondo se usan `text-stack-text` o `text-stack-primary`.
export function buildStackTheme(stackColors = {}) {
    const stack = { ...DEFAULT_STACK };
    for (const key of Object.keys(DEFAULT_STACK)) {
        if (isHexColor(stackColors?.[key])) {
            stack[key] = stackColors[key];
        }
    }

    const dark = luminance(stack.background) < 0.42;
    const ink = luminance(stack.emphasis) > 0.6 ? "#141210" : "#FFFFFF";

    const cssVars = {
        "--color-stack-primary": stack.primary,
        "--color-stack-emphasis": stack.emphasis,
        "--color-stack-text": stack.text,
        "--color-stack-background": stack.background,
        "--color-stack-ink": ink,
        "--color-stack-text-sub": mix(
            stack.text,
            stack.background,
            dark ? 0.78 : 0.72
        ),
        "--color-stack-line": alpha(stack.text, dark ? 0.16 : 0.1),
        "--color-stack-tile-bg": dark
            ? alpha("#FFFFFF", 0.08)
            : mix(stack.text, stack.background, 0.06),
        "--color-stack-page-bg": mix(stack.background, "#000000", 0.45)
    };

    return { stack, dark, cssVars };
}

export function buildCatalogPreviewViewModel(catalog) {
    const { cssVars, dark, stack } = buildStackTheme(catalog?.stackColors);

    const socialNetworks = catalog?.socialNetworks ?? [];
    const socialEntries = socialNetworks
        .filter((entry) => Boolean(entry?.url))
        .map((entry) => ({
            network: entry.network,
            url: entry.url,
            label: SOCIAL_LABELS[entry.network] ?? entry.network
        }));

    const segments = catalog?.segments ?? [];

    const products = (catalog?.products ?? []).filter(
        (product) => product.active
    );

    return {
        businessName: catalog?.businessName ?? "Catálogo",
        displayMode: catalog?.displayMode ?? "catalog",
        businessDescription: catalog?.businessDescription ?? "",
        logoIconUrl: catalog?.logoIconUrl ?? catalog?.logoHorizontalUrl ?? "",
        stack,
        dark,
        cssVars,
        socialEntries,
        segments,
        products
    };
}
